import {
  resolveNonEmailDisplayName,
  resolveUserDisplayNameOrUnknown,
  UNKNOWN_USER_DISPLAY_NAME,
  type UserDisplayNameFields,
} from "~/user/utils/format-user-name";
import { validateEmail } from "~/utils/strings";

export type SharedAccessUser = {
  id?: string;
  userId?: string;
  subjectId?: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  label?: string | null;
  email?: string | null;
  userName?: string | null;
  subjectInfo?: {
    email?: string | null;
    username?: string | null;
  } | null;
};

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
}

function sharedAccessEmail(user: SharedAccessUser): string | undefined {
  const direct = user.email ?? user.subjectInfo?.email ?? undefined;
  if (direct?.trim()) {
    return direct.trim();
  }
  const label = user.label?.trim();
  if (label && validateEmail(label)) {
    return label;
  }
  return undefined;
}

function sharedAccessNameFields(user: SharedAccessUser): UserDisplayNameFields {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    displayName: user.displayName,
    userName: user.userName ?? user.subjectInfo?.username ?? undefined,
    email: sharedAccessEmail(user),
  };
}

function resolveSharedAccessDisplayName(user: SharedAccessUser): string {
  const fields = sharedAccessNameFields(user);
  const nonEmailName = resolveNonEmailDisplayName(fields, user.label);
  if (nonEmailName !== UNKNOWN_USER_DISPLAY_NAME) {
    return nonEmailName;
  }
  return resolveUserDisplayNameOrUnknown(fields);
}

export function getSharedUserDisplayName(user: SharedAccessUser, isCurrentUser = false): string {
  const base = resolveSharedAccessDisplayName(user);
  return isCurrentUser ? `${base} (You)` : base;
}

export function getSharedUserInitials(user: SharedAccessUser): string {
  const displayName = resolveSharedAccessDisplayName(user);
  if (displayName === UNKNOWN_USER_DISPLAY_NAME) {
    return "?";
  }
  if (validateEmail(displayName)) {
    return displayName[0]!.toUpperCase();
  }
  return initialsFromName(displayName);
}
